import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  alpha,
} from "@mui/material";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import styles from "./Sidebar.module.css";
import logo1 from "../../assets/images/GutBuddy2.svg";

// Icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import GroupIcon from "@mui/icons-material/Group";
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import InsightsIcon from '@mui/icons-material/Insights';
import BookIcon from '@mui/icons-material/Book';
import SummarizeIcon from "@mui/icons-material/Summarize";
import DinnerDiningIcon from "@mui/icons-material/DinnerDining";
import PsychologyIcon from "@mui/icons-material/Psychology";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import LiveHelpIcon from "@mui/icons-material/LiveHelp";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CreateIcon from '@mui/icons-material/Create';
import StorageIcon from '@mui/icons-material/Storage';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BookIcon2 from '@mui/icons-material/MenuBook';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import FeedbackIcon from '@mui/icons-material/Feedback';
import ChatIcon from '@mui/icons-material/Chat';
import WebIcon from '@mui/icons-material/Web';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import RateReviewIcon from '@mui/icons-material/RateReview';
import ArticleIcon from '@mui/icons-material/Article';
import GroupsIcon from '@mui/icons-material/Groups';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import LandscapeIcon from "@mui/icons-material/Landscape";
import LayersIcon from "@mui/icons-material/Layers";
import EventNoteIcon from '@mui/icons-material/EventNote';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import FeaturedPlayListIcon from '@mui/icons-material/FeaturedPlayList';
import MovieIcon from '@mui/icons-material/Movie';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import DoctorImg from "../../assets/images/medical-assistance.svg";

// Hierarchical Sidebar Structure with Colors
const sidebarStructure = [
  {
    label: "Dashboard & Analytics",
    icon: <DashboardIcon />,
    color: "#4A90E2", // Light Blue
    subSections: [
      { label: "Dashboard", url: "/dashboard", icon: <DashboardIcon /> },
      { label: "System Analytics", url: "/system-analytics", icon: <AnalyticsIcon /> }
    ]
  },
  {
    label: "People Management",
    icon: <GroupIcon />,
    color: "#E91E63", // Pink
    subSections: [
      {
        label: "Patients",
        subSubSections: [
          { label: "Patient List", url: "/Patient", icon: <GroupIcon /> },
          { label: "Test Reports", url: "/reports", icon: <SummarizeIcon /> }
        ]
      },
      {
        label: "Doctors",
        subSubSections: [
          { label: "Doctor List", url: "/doctor", icon: DoctorImg, isImage: true, iconSrc: DoctorImg },
          { label: "Enroll Doctor", url: "/enrolldoctor", icon: <PersonAddIcon /> },
          { label: "Specialties", url: "/doctorspeciality", icon: <InsightsIcon /> }
        ]
      },
      {
        label: "In-Person Bookings",
        url: "/inpersonappointment",
        icon: <BookIcon />
      }
    ]
  },
  {
    label: "Content Management",
    icon: <StorageIcon />,
    color: "#FF9800", // Orange
    subSections: [
      {
        label: "Diet",
        subSubSections: [
          { label: "Diet Content", url: "/diet", icon: <DinnerDiningIcon /> },
          { label: "Diet Meal Plan", url: "/DietMealPlan", icon: <DinnerDiningIcon /> },
          { label: "Meal Plan Review", url: "/diet-meal-plan-review", icon: <DinnerDiningIcon /> },
          { label: "Master Food", url: "/MasterFood", icon: <DinnerDiningIcon /> },
          { label: "Diet Categories", url: "/dietcategories", icon: <LandscapeIcon /> },
          { label: "Diet Sub Categories", url: "/dietsubcategories", icon: <FactCheckIcon /> },
          { label: "Diet Prompts", url: "/diet-prompts", icon: <CreateIcon /> },
          { label: "System Overview", url: "/diet-system-overview", icon: <AnalyticsIcon /> },
          { label: "Diet Feedback", url: "/diet-feedback", icon: <FeedbackIcon /> }
        ]
      },
      {
        label: "CBT",
        subSubSections: [
          { label: "CBT Question", url: "/cbtquestion", icon: <HelpOutlineIcon /> },
          { label: "Dass Question", url: "/cbtdassquestion", icon: <HelpOutlineIcon /> },
          { label: "CBT Tasks", url: "/cbt-tasks", icon: <AssignmentIcon /> },
          { label: "Prompt Management", url: "/prompts", icon: <CreateIcon /> },
          { label: "Session Summaries", url: "/cbt-session-summaries", icon: <BookIcon2 /> },
          { label: "Journal Categories", url: "/journal-categories", icon: <BookIcon2 /> },
          { label: "Journal Responses", url: "/journal-responses", icon: <BookIcon2 /> },
          { label: "CBT Content", url: "/cbt-content", icon: <PsychologyIcon /> },
          { label: "CBT Analytics", url: "/cbt-analytics", icon: <AnalyticsIcon /> }
        ]
      },
      {
        label: "Gut Content",
        url: "/brain",
        icon: <PsychologyIcon />
      },
      {
        label: "Learn Content",
        url: "/blogContnent",
        icon: <RssFeedIcon />
      },
      {
        label: "FAQ",
        url: "/faq",
        icon: <LiveHelpIcon />
      },
      {
        label: "Gut Categories",
        url: "/gutcategories",
        icon: <LayersIcon />
      },
      {
        label: "Learn Categories",
        url: "/blogcategories",
        icon: <DashboardIcon />
      },
      {
        label: "Learn Sub Categories",
        url: "/blogsubcategories",
        icon: <EventNoteIcon />
      },
      {
        label: "Gut Sub Categories",
        url: "/gutsubcategories",
        icon: <FeaturedPlayListIcon />
      }
    ]
  },
  {
    label: "Website Management",
    icon: <WebIcon />,
    color: "#4CAF50", // Green
    subSections: [
      {
        label: "Home Page",
        subSubSections: [
          { label: "Home Page", url: "/homepage", icon: <HomeIcon /> },
          { label: "Upload Videos", url: "/uploadvideo", icon: <MovieIcon /> },
          { label: "Carousel", url: "/homepage/carousel", icon: <HomeIcon /> },
          { label: "Brand Logos", url: "/homepage/brand-logos", icon: <HomeIcon /> }
        ]
      },
      {
        label: "About Page",
        subSubSections: [
          { label: "Upload Videos", url: "/about/uploadvideos", icon: <MovieIcon /> },
          { label: "Upload Logos", url: "/about/uploadlogos", icon: <InfoIcon /> },
          { label: "Hero Section", url: "/about/herosection", icon: <InfoIcon /> }
        ]
      },
      {
        label: "User Queries",
        url: "/userqueries",
        icon: <HelpOutlineIcon />
      },
      {
        label: "Score Enquiry",
        url: "/gut-score-enquiry",
        icon: <QueryStatsIcon />
      },
      {
        label: "Testimonial",
        subSubSections: [
          { label: "User Testimonial", url: "/usertestimonial", icon: <RateReviewIcon /> },
          { label: "Doctor Testimonial", url: "/doctortestimonial", icon: <RateReviewIcon /> }
        ]
      },
      {
        label: "Blogs",
        subSubSections: [
          { label: "Manage Blogs", url: "/blogs", icon: <ArticleIcon /> },
          { label: "Webinar Events", url: "/webinarevents", icon: <ArticleIcon /> }
        ]
      },
      {
        label: "Teams",
        url: "/teams",
        icon: <GroupsIcon />
      },
      {
        label: "Q&A",
        url: "/q&a",
        icon: <QuestionAnswerIcon />
      }
    ]
  },
  {
    label: "System Settings",
    icon: <FeedbackIcon />,
    color: "#9C27B0", // Purple
    subSections: [
      { label: "User Feedback", url: "/feedback", icon: <FeedbackIcon /> },
      { label: "Broadcast Notification", url: "/broadcast-notification", icon: <ChatIcon /> },
      { label: "Yoga Module", url: "/yogamodule", icon: <SelfImprovementIcon /> }
    ]
  }
];

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // State for managing open/closed sections
  const [openSections, setOpenSections] = useState({});
  const [openSubSections, setOpenSubSections] = useState({});
  const [openSubSubSections, setOpenSubSubSections] = useState({});

  // Auto-open sections based on current path
  useEffect(() => {
    const currentPath = location.pathname;
    
    sidebarStructure.forEach((section) => {
      // Check if current path matches any item in this section
      const isInSection = section.subSections?.some(subSection => {
        if (subSection.url === currentPath) return true;
        if (subSection.subSubSections) {
          return subSection.subSubSections.some(subSubSection => {
            if (subSubSection.url === currentPath) return true;
            if (subSubSection.subSubSubSections) {
              return subSubSection.subSubSubSections.some(item => item.url === currentPath);
            }
            return false;
          });
        }
        if (subSection.subSubSubSections) {
          return subSection.subSubSubSections.some(item => item.url === currentPath);
        }
        return false;
      });

      if (isInSection) {
        setOpenSections(prev => ({ ...prev, [section.label]: true }));
        
        // Open the specific sub-section
        section.subSections?.forEach(subSection => {
          if (subSection.url === currentPath) {
            setOpenSubSections(prev => ({ ...prev, [subSection.label]: true }));
          } else if (subSection.subSubSections) {
            const isInSubSub = subSection.subSubSections.some(subSubSection => {
              if (subSubSection.url === currentPath) return true;
              if (subSubSection.subSubSubSections) {
                return subSubSection.subSubSubSections.some(item => item.url === currentPath);
              }
              return false;
            });
            if (isInSubSub) {
              setOpenSubSections(prev => ({ ...prev, [subSection.label]: true }));
              subSection.subSubSections.forEach(subSubSection => {
                if (subSubSection.url === currentPath || 
                    subSubSection.subSubSubSections?.some(item => item.url === currentPath)) {
                  setOpenSubSubSections(prev => ({ ...prev, [subSubSection.label]: true }));
                }
              });
            }
          }
        });
      }
    });
  }, [location.pathname]);

  const handleSectionClick = (sectionLabel) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionLabel]: !prev[sectionLabel]
    }));
  };

  const handleSubSectionClick = (subSectionLabel) => {
    setOpenSubSections(prev => ({
      ...prev,
      [subSectionLabel]: !prev[subSectionLabel]
    }));
  };

  const handleSubSubSectionClick = (subSubSectionLabel) => {
    setOpenSubSubSections(prev => ({
      ...prev,
      [subSubSectionLabel]: !prev[subSubSectionLabel]
    }));
  };

  const handleItemClick = (item, level) => {
    if (level === 0) {
      handleSectionClick(item.label);
    } else if (level === 1) {
      if (item.subSubSections || item.subSubSubSections) {
        handleSubSectionClick(item.label);
      }
    } else if (level === 2) {
      if (item.subSubSubSections) {
        handleSubSubSectionClick(item.label);
      }
    }
  };

  const isActive = (url) => {
    if (!url) return false;
    if (url === '/') {
      return location.pathname === url;
    }
    return location.pathname === url;
  };

  const renderIcon = (icon, isImage = false, iconSrc = null) => {
    if (isImage && iconSrc) {
      return <img src={iconSrc} alt="icon" style={{ width: "24px", height: "24px" }} />;
    }
    return icon;
  };

  // Helper function to get a valid color for an item, traversing up the hierarchy
  const getItemColor = (item, level) => {
    // If item has its own color, use it
    if (item.color) {
      return item.color;
    }
    
    // If it's a top-level section, find it in sidebarStructure
    if (level === 0) {
      const section = sidebarStructure.find(s => s.label === item.label);
      return section?.color || '#666666'; // Default gray color
    }
    
    // For nested items, find the parent section's color
    for (const section of sidebarStructure) {
      const findInSubSections = (subSections, depth = 0) => {
        if (!subSections) return null;
        
        for (const subSection of subSections) {
          if (subSection.label === item.label) {
            return section.color || '#666666';
          }
          if (subSection.subSubSections) {
            const found = findInSubSections(subSection.subSubSections, depth + 1);
            if (found) return found;
          }
          if (subSection.subSubSubSections) {
            const found = findInSubSections(subSection.subSubSubSections, depth + 1);
            if (found) return found;
          }
        }
        return null;
      };
      
      const foundColor = findInSubSections(section.subSections);
      if (foundColor) return foundColor;
    }
    
    // Default fallback color
    return '#666666';
  };

  const renderMenuItem = (item, level = 0, parentColor = null) => {
    const hasChildren = item.subSections || item.subSubSections || item.subSubSubSections;
    const isOpen = level === 0 ? openSections[item.label] : 
                   level === 1 ? openSubSections[item.label] : 
                   openSubSubSections[item.label];
    const paddingLeft = 2 + level * 2;
    // Ensure we always have a valid color (not 'inherit')
    let itemColor = item.color || parentColor || getItemColor(item, level);
    if (itemColor === 'inherit' || !itemColor || typeof itemColor !== 'string' || !itemColor.startsWith('#')) {
      itemColor = parentColor || '#666666';
    }

    // If it's a direct link (has url and no children), render as NavLink
    if (item.url && !hasChildren) {
      return (
        <NavLink
          key={item.label}
          to={item.url}
          style={{ textDecoration: "none" }}
        >
          <ListItem
            button
            sx={{ 
              pl: paddingLeft, 
              '&.Mui-selected': {
                backgroundColor: alpha(itemColor, 0.1),
                borderLeft: `4px solid ${itemColor}`,
                color: itemColor,
                '& .MuiListItemIcon-root': {
                  color: itemColor,
                },
              },
              '&:hover': {
                backgroundColor: alpha(itemColor, 0.05),
                color: itemColor,
                '& .MuiListItemIcon-root': {
                  color: itemColor,
                },
              },
              color: isActive(item.url) ? itemColor : 'inherit',
            }}
            className={isActive(item.url) ? styles.active_icon : styles.inactive_icon}
          >
            <ListItemIcon sx={{ color: isActive(item.url) ? itemColor : 'inherit' }}>
              {renderIcon(item.icon, item.isImage, item.iconSrc)}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        </NavLink>
      );
    }

    // If it has children, render as collapsible item
    return (
      <React.Fragment key={item.label}>
        <ListItem
          button
          onClick={() => handleItemClick(item, level)}
          sx={{ 
            pl: paddingLeft, 
            '&.Mui-selected': {
              backgroundColor: alpha(itemColor, 0.1),
              borderLeft: `4px solid ${itemColor}`,
              color: itemColor,
              '& .MuiListItemIcon-root': {
                color: itemColor,
              },
            },
            '&:hover': {
              backgroundColor: alpha(itemColor, 0.05),
              color: itemColor,
              '& .MuiListItemIcon-root': {
                color: itemColor,
              },
            },
            color: 'inherit',
          }}
          className={styles.inactive_icon}
        >
          <ListItemIcon sx={{ color: itemColor }}>
            {renderIcon(item.icon, item.isImage, item.iconSrc)}
          </ListItemIcon>
          <ListItemText primary={item.label} />
          {hasChildren && (isOpen ? <ExpandLess /> : <ExpandMore />)}
        </ListItem>
        
        {hasChildren && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.subSections?.map(subSection => renderMenuItem(subSection, level + 1, itemColor))}
              {item.subSubSections?.map(subSubSection => renderMenuItem(subSubSection, level + 1, itemColor))}
              {item.subSubSubSections?.map(subSubSubSection => renderMenuItem(subSubSubSection, level + 2, itemColor))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const logoutHandler = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  return (
    <Box className={styles.main_div}>
      <Box className={styles.logo_content}>
        <Box className={styles.header_div}>
          <img src={logo1} alt="logo" className={styles.logo} />
        </Box>
      </Box>

      <List className={styles.list_div}>
        {sidebarStructure.map((section) => renderMenuItem(section, 0))}
      </List>

      <Box className={styles.logout_div}>
        <Button
          onClick={logoutHandler}
          variant="contained"
          className={styles.logout_btn}
        >
          logout
        </Button>
      </Box>
    </Box>
  );
};

export default Sidebar;
